import { useCallback, useEffect, useRef, useState } from 'react';
import { issueListening, listeningBytes, STAFF_LISTENING_CONTRACT } from './listeningApi';

let activePlayback = null;
const initial = { status: 'idle', choiceMedia: {} };

export default function useListeningPlayback({ roundId, question, boardSessionId, boardToken, staffSchoolId, disabled }) {
  const [state, setState] = useState(initial);
  const operation = useRef(0);
  const resources = useRef({ controller: null, audio: null, urls: [], timeout: null });
  const playingBefore = useRef(false);
  const stop = useCallback((keepUrls = false) => {
    operation.current += 1;
    const current = resources.current;
    current.controller?.abort();
    clearTimeout(current.timeout);
    if (current.audio) {
      current.audio.onplaying = null;
      current.audio.onended = null;
      current.audio.onerror = null;
      current.audio.onpause = null;
      current.audio.pause();
      current.audio.removeAttribute('src');
      current.audio.load();
    }
    if (!keepUrls) current.urls.forEach((url) => URL.revokeObjectURL(url));
    resources.current = { controller: null, audio: null, urls: keepUrls ? current.urls : [], timeout: null };
    if (activePlayback?.stop === stop) activePlayback = null;
  }, []);

  useEffect(() => {
    stop();
    playingBefore.current = false;
    setState(initial);
    return stop;
  }, [roundId, question?.id, question?.key, boardSessionId, boardToken, staffSchoolId, stop]);

  useEffect(() => {
    if (disabled) {
      // Staff preview feedback must retain its picture choices. All URLs are
      // still released by question navigation, replay or unmount cleanup.
      stop(Boolean(staffSchoolId));
      if (!staffSchoolId) setState(initial);
    }
  }, [disabled, staffSchoolId, stop]);

  useEffect(() => {
    const interrupt = () => {
      if (resources.current.audio || resources.current.controller) {
        stop(); setState({ status: 'interrupted', choiceMedia: {} });
      }
    };
    const hidden = () => { if (document.hidden) interrupt(); };
    window.addEventListener('pagehide', interrupt);
    document.addEventListener('visibilitychange', hidden);
    return () => {
      window.removeEventListener('pagehide', interrupt);
      document.removeEventListener('visibilitychange', hidden);
    };
  }, [stop]);

  const play = useCallback(async () => {
    const staff = Boolean(staffSchoolId && question?.listening?.contract === STAFF_LISTENING_CONTRACT);
    if (disabled || !question?.listening || (!staff && !boardSessionId)) return;
    activePlayback?.interrupt();
    stop();
    const generation = operation.current;
    const current = () => generation === operation.current;
    const controller = new AbortController();
    resources.current.controller = controller;
    activePlayback = { stop, interrupt: () => { stop(); setState({ status: 'interrupted', choiceMedia: {} }); } };
    setState({ status: 'loading', choiceMedia: {} });
    const context = { boardSessionId, boardToken, signal: controller.signal,
      ...(staff ? { staffSchoolId, staffQuestionKey: question.key } : {}) };
    resources.current.timeout = setTimeout(() => {
      if (current()) { stop(); setState({ status: 'error', choiceMedia: {} }); }
    }, 15000);
    try {
      let audioBlob;
      let choiceMedia;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const delivery = await issueListening(roundId, question.id,
          attempt ? 'refresh' : playingBefore.current ? 'replay' : 'play', context);
        if (!current()) return;
        try {
          const entries = Object.entries(delivery.choice_media || {});
          const blobs = await Promise.all([listeningBytes(roundId, question.id, delivery.audio, context),
            ...entries.map(([, asset]) => listeningBytes(roundId, question.id, asset, context))]);
          if (!current()) return;
          [audioBlob] = blobs;
          choiceMedia = Object.fromEntries(entries.map(([choice], index) => {
            const url = URL.createObjectURL(blobs[index + 1]);
            resources.current.urls.push(url);
            return [choice, { url, alt_text: `Choice ${question.choices.indexOf(choice) + 1}` }];
          }));
          break;
        } catch (error) {
          if (error.code !== 'AUDIO_HANDLE_EXPIRED' || attempt !== 0) throw error;
        }
      }
      if (!current()) return;
      const url = URL.createObjectURL(audioBlob);
      resources.current.urls.push(url);
      const audio = new Audio(url);
      resources.current.audio = audio;
      audio.preload = 'none';
      audio.onplaying = () => {
        if (!current()) return;
        clearTimeout(resources.current.timeout);
        playingBefore.current = true;
        setState({ status: 'playing', choiceMedia });
      };
      audio.onended = () => { if (current()) setState({ status: 'ended', choiceMedia }); };
      audio.onerror = () => {
        if (current()) { stop(); setState({ status: 'error', choiceMedia: {} }); }
      };
      audio.onpause = () => {
        if (current() && !audio.ended) setState({ status: 'interrupted', choiceMedia });
      };
      await audio.play();
    } catch (error) {
      if (!current()) return;
      stop();
      setState({ status: error.retryable === false ? 'blocked' : 'error', choiceMedia: {} });
    }
  }, [disabled, question, boardSessionId, boardToken, staffSchoolId, roundId, stop]);

  return { ...state, play };
}
